import { ArrowRightIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Tr,
  Input,
  Divider
} from "@chakra-ui/react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  PaginationState,
  getPaginationRowModel,
} from "@tanstack/react-table";
// Custom components
import Card from "components/card/Card";
import { useState, useEffect, useRef, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteUserRequest, updateUserRequest } from "store/user/reducer";
import { allUsersSelector, isGettingAllUsersSelector } from "store/user/selector";
import DeleteButtopPopup from "./DeleteButtonPopup";
// Assets

type RowObj = {
  id: string;
  name: string;
  email: string;
  phone: string;
  languages: string[];
  actionCol?: string;
};

const columnHelper = createColumnHelper<RowObj>();

// const columns = columnsDataCheck;
export default function TranslatorsMobileTable(props: {
  tableData: any;
  pageLimit: number;
  currentPage: number;
  onSelectRow: Function;
}) {
  const dispatch = useDispatch();
  const { tableData, onSelectRow, pageLimit, currentPage } = props;
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(
    null
  );
  const [editingColumnId, setEditingColumnId] = useState<string | null>(
    null
  );
  const isGettingAllUsers = useSelector(isGettingAllUsersSelector);
  const allUsers = useSelector(allUsersSelector);

  const inputRefs = useRef<{
    [key: string]: HTMLInputElement;
  }>({});

  const handleInputChange = (
    rowIndex: number,
    columnId: keyof RowObj,
    value: string | number
  ) => {
    setEditingColumnId(columnId);
    setRawData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  const handleToggleEdit = (rowIndex: number) => {
    setEditingRowIndex(rowIndex === editingRowIndex ? null : rowIndex);
    setRawData(data);
  };

  const handleDelete = (rowIndex: number) => {
    const userRow = rawData[rowIndex];

    dispatch(
      deleteUserRequest({
        user_id: userRow?.id
      })
    )
    setData(() => (
      rawData.filter((_, index) => index !== rowIndex)
    ));
  }

  const handleSaveData = (rowIndex: number) => {
    // api call to save data;
    const userRow = rawData[rowIndex];

    dispatch(
      updateUserRequest({
        user_id: userRow?.id,
        user_details: {
          first_name: userRow?.name?.trim().split(' ')[0] ?? "",
          last_name: userRow?.name?.trim().split(' ')[1] ?? "",
          email: userRow?.email,
          phone: userRow?.phone
        },
        no_after_call: true
      })
    )
    setData(rawData);
    initializeEditSetting();
  };

  const initializeEditSetting = () => {
    setEditingColumnId(null);
    setEditingRowIndex(null);
  };

  let defaultData = tableData;
  const columns = [
    columnHelper.accessor("name", {
      id: "name",
      header: () => (
        <Text
          textAlign="left"
          fontSize="14px"
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Name
        </Text>
      ),
      cell: ({ row, getValue }) => {
        const isEditing = editingRowIndex === row.index;
        return isEditing ? (
          <Input
            ref={(el) => (inputRefs.current[row.index + "name"] = el!)}
            value={rawData[row.index].name}
            onChange={(e) =>
              handleInputChange(row.index, "name", e.target.value)
            }
          />
        ) : (
          <Text
            fontSize={"14px"}
            lineHeight="24px"
            fontWeight={"400"}
            color="black"
          >
            {getValue()}
          </Text>
        );
      },
    }),
    columnHelper.accessor("email", {
      id: "email",
      header: () => (
        <Text
          textAlign="left"
          fontSize="14px"
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Email
        </Text>
      ),
      cell: ({ row, getValue }) => {
        const isEditing = editingRowIndex === row.index;
        return isEditing ? (
          <Input
            ref={(el) => (inputRefs.current[row.index + "email"] = el!)}
            value={rawData[row.index].email}
            onChange={(e) =>
              handleInputChange(row.index, "email", e.target.value)
            }
          />
        ) : (
          <Text
            fontSize={"14px"}
            lineHeight="24px"
            fontWeight={"400"}
            color="black"
          >
            {getValue()}
          </Text>
        );
      },
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => (
        <Text
          textAlign="left"
          fontSize="14px"
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Phone
        </Text>
      ),
      cell: ({ row, getValue }) => {
        const isEditing = editingRowIndex === row.index;
        return isEditing ? (
          <Input
            ref={(el) => (inputRefs.current[row.index + "phone"] = el!)}
            value={rawData[row.index].phone}
            onChange={(e) =>
              handleInputChange(row.index, "phone", e.target.value)
            }
          />
        ) : (
          <Text
            fontSize={"14px"}
            lineHeight="24px"
            fontWeight={"400"}
            color="black"
          >
            {getValue()}
          </Text>
        );
      },
    }),
    columnHelper.accessor("languages", {
      id: "languages",
      header: () => (
        <Text
          textAlign="left"
          fontSize="14px"
          lineHeight="24px"
          fontWeight="400"
          color="gray.500"
        >
          Languages
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Text
            fontSize={"14px"}
            lineHeight="24px"
            fontWeight={"400"}
            color="black"
          >
            {info.getValue().join(", ")}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor("actionCol", {
      id: "actionCol",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          lineHeight="24px"
          color="gray.500"
          fontWeight="400"
        ></Text>
      ),
      cell: ({ row }) => {
        const isEditing = editingRowIndex === row.index;
        return (
          <>
            {!isEditing ? (
              <Flex align="center" ml="0">
                <Button
                  borderRadius="0px"
                  width="100%"
                  padding="15px 35px"
                  bgColor="#2F80ED"
                  color="#FFF"
                  fontSize="14px"
                  lineHeight="17px"
                  fontWeight="bold"
                  onClick={(e) => {
                    handleToggleEdit(row.index);
                  }}
                >
                  Update
                </Button>
                <Divider orientation="vertical" />
                <DeleteButtopPopup displayMode="mobile" onOk={() => handleDelete(row.index)} />
                <Divider orientation="vertical" />
                <Button
                  borderRadius="0px"
                  width="100%"
                  padding="15px 35px"
                  bgColor="#2F80ED"
                  color="#FFF"
                  fontSize="14px"
                  lineHeight="17px"
                  fontWeight="bold"
                  onClick={(e) => {
                    onSelectRow(row.original.id);
                  }}
                >
                  View Details
                  {/* <ArrowRightIcon ml="10px" fontSize="8px" /> */}
                </Button>
              </Flex>
            ) : (
              <Flex align="center" ml="0">
                <Button
                  borderRadius="0px"
                  width="100%"
                  padding="15px 35px"
                  bgColor="#2F80ED"
                  color="#FFF"
                  fontSize="14px"
                  lineHeight="17px"
                  fontWeight="bold"
                  onClick={(e) => {
                    handleSaveData(row.index);
                  }}
                >
                  Save
                </Button>
                <Divider orientation="vertical" />
                <Button
                  borderRadius="0px"
                  width="100%"
                  padding="15px 35px"
                  bgColor="#2F80ED"
                  color="#FFF"
                  fontSize="14px"
                  lineHeight="17px"
                  fontWeight="bold"
                  onClick={(e) => {
                    initializeEditSetting();
                  }}
                >
                  Cancel
                  {/* <ArrowRightIcon ml="10px" fontSize="8px" /> */}
                </Button>
              </Flex>
            )}
          </>
        );
      },
    }),
  ];
  const [data, setData] = useState(() => [...defaultData]);
  const [rawData, setRawData] = useState(() => [...data]);

  useEffect(() => {
    setData([...defaultData]);
  }, [defaultData]);

  useEffect(() => {
    setRawData([...data]);
  }, [data]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    debugTable: true,
  });

  useEffect(() => {
    if(pageLimit) table.setPageSize(pageLimit);
  }, [pageLimit])

  useEffect(() => {
    if(currentPage) table.setPageIndex(currentPage-1);
  }, [currentPage])

  useEffect(() => {
    if (
      editingRowIndex != null &&
      editingColumnId != null &&
      inputRefs.current[editingRowIndex + editingColumnId]
    ) {
      inputRefs.current[editingRowIndex + editingColumnId].focus();
    }
  }, [rawData, editingRowIndex, editingColumnId]);

  return (
    <Card
      flexDirection="column"
      p="0px"
      px="10px"
      w="calc(100% + 20px)"
      ml="-10px"
      overflowX="auto"
    >
      <Box>
        <Table variant="simple" color="gray.500" mb="24px">
          <Tbody>
            {!isGettingAllUsers &&
              table
                .getRowModel()
                .rows
                .map((row: any, index: number) => {
                  return (
                    <Table
                      key={index}
                      mt={index === 0 ? "10px" : "35px"}
                      border="1px solid #dcdcdc"
                    >
                      <Tbody>
                        {row
                          .getVisibleCells()
                          .map((cell: any, _index: number) => {
                            return (
                              <Tr key={_index}>
                                {_index < 4 ? (
                                  <Fragment>
                                    <Td
                                      p="10px"
                                      width="110px"
                                      borderColor="#dcdcdc"
                                      borderRight="1px solid #dcdcdc"
                                      bgColor="#FAFAFA"
                                    >
                                      {flexRender(
                                        table.getHeaderGroups()[0].headers[
                                          _index
                                        ].column.columnDef.header,
                                        table
                                          .getHeaderGroups()[0]
                                          .headers[_index].getContext()
                                      )}
                                    </Td>
                                    <Td p="10px" position="relative">
                                      {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                      )}
                                    </Td>
                                  </Fragment>
                                ) : (
                                  <Td colSpan={2} padding="0px">
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext()
                                    )}
                                  </Td>
                                )}
                              </Tr>
                            );
                          })}
                      </Tbody>
                    </Table>
                  );
                })}
            {isGettingAllUsers &&
              [1, 2, 3, 4, 5].map((row) => {
                return (
                  <Table
                    key={row}
                    mt={row === 0 ? "10px" : "35px"}
                    border="1px solid #dcdcdc"
                  >
                    <Tbody>
                      {[1, 2, 3, 4, 5].map((_it) => (
                        <Tr key={_it}>
                          <Td
                            p="10px"
                            width="110px"
                            borderColor="#dcdcdc"
                            borderRight="1px solid #dcdcdc"
                            bgColor="#FAFAFA"
                          >
                            <Skeleton height="24px" width="100%" />
                          </Td>
                          <Td p="10px" borderColor="#dcdcdc">
                            <Skeleton height="24px" width="100%" />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                );
              })}
          </Tbody>
        </Table>
      </Box>
    </Card>
  );
}
